import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Organization } from '../models/organization/organization.interface';
import { DetailedOrganization } from '../models/organization/detailed-organization.interface';
import { OrganizationFormValue } from '../models/organization/organization-form-group.interface';
import { MinimalOrganization } from '../models/organization/minimal-organization.interface';
import { RequestModel } from '../models/request-model.interface';
import { PagedList } from '../models/paged-list.interface';
import { SalesOrderDetails } from '../models/sales/sales-order-details.interface';

@Injectable()
export class OrganizationService {
  private httpClient = inject(HttpClient);

  private organizationsUrl = '';

  public setOrganizationsUrl(companyId: string) {
    this.organizationsUrl = `${environment.apiUrl}/companies/${companyId}/organizations`;
  }

  public getCompanyOrganizations(): Observable<Organization[]> {
    return this.httpClient.get<Organization[]>(this.organizationsUrl).pipe(
      catchError((error: unknown) => {
        throw new Error(`An error occurred: ${error}`);
      }),
    );
  }

  public getCompanyOrganizationsPaged(
    request: RequestModel,
  ): Observable<PagedList<Organization>> {
    return this.httpClient
      .post<PagedList<Organization>>(
        `${this.organizationsUrl}/company-organizations`,
        request,
      )
      .pipe(
        catchError((error: unknown) => {
          throw new Error(`An error occurred: ${error}`);
        }),
      );
  }

  public getMinimalOrganizations(): Observable<MinimalOrganization[]> {
    return this.httpClient
      .get<MinimalOrganization[]>(`${this.organizationsUrl}/minimal`)
      .pipe(
        catchError((error: unknown) => {
          throw new Error(`An error occurred: ${error}`);
        }),
      );
  }

  public getOrganization(
    organizationId: string,
  ): Observable<DetailedOrganization> {
    return this.httpClient
      .get<DetailedOrganization>(`${this.organizationsUrl}/${organizationId}`)
      .pipe(
        catchError((error: unknown) => {
          throw new Error(`An error occurred: ${error}`);
        }),
      );
  }

  public getOrganizationSalesOrder(
    organizationId: string,
    salesOrderId: string,
  ): Observable<SalesOrderDetails> {
    return this.httpClient
      .get<SalesOrderDetails>(
        `${this.organizationsUrl}/${organizationId}/sales-orders/${salesOrderId}`,
      )
      .pipe(
        catchError((error: unknown) => {
          throw new Error(`An error occurred: ${error}`);
        }),
      );
  }

  public createOrganization(
    organization: OrganizationFormValue,
  ): Observable<Organization> {
    return this.httpClient
      .post<Organization>(this.organizationsUrl, organization)
      .pipe(
        catchError((error: unknown) => {
          throw new Error(`An error occurred: ${error}`);
        }),
      );
  }

  public patchOrganization(
    organizationId: string,
    field: string,
    value: unknown,
  ): Observable<null> {
    const patchPayload = { [field]: value };

    return this.httpClient.patch<null>(
      `${this.organizationsUrl}/${organizationId}`,
      patchPayload,
      { headers: { 'Content-Type': 'application/merge-patch+json' } },
    );
  }

  public deleteOrganization(organizationId: string): Observable<null> {
    return this.httpClient
      .delete<null>(`${this.organizationsUrl}/${organizationId}`)
      .pipe(
        catchError((error: unknown) => {
          throw new Error(`An error occurred: ${error}`);
        }),
      );
  }
}
